import { useEffect, useState, type FormEvent } from 'react';
import { crypto as cv, type dto } from '@photo-vault/shared';
import { Button, Card, Icon, Input } from '../../ui/index.js';
import { useAuthSession } from '../../auth/AuthContext.js';
import { useInfra } from '../../infra/context.js';

interface Props {
  rootAlbumId: string;
  albumKey: CryptoKey;
}

// Owner-only section embedded in AlbumDetailPage when the current album is a
// root. Lets the user share the root with a recipient (looked up by email)
// and revoke existing shares.
export function ShareSection({ rootAlbumId, albumKey }: Props) {
  const session = useAuthSession();
  const { metadata } = useInfra();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shares, setShares] = useState<dto.OutgoingShare[] | 'loading' | { error: string }>(
    'loading',
  );

  async function reload() {
    setShares('loading');
    try {
      const out = await metadata.listOutgoingShares(rootAlbumId, session.jwt);
      setShares(out.shares);
    } catch (err) {
      setShares({ error: err instanceof Error ? err.message : 'errore' });
    }
  }

  useEffect(() => {
    void reload();
  }, [rootAlbumId]);

  async function handleShare(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError('inserisci una email');
      return;
    }
    if (trimmed === session.email.toLowerCase()) {
      setError('non puoi condividere con te stesso');
      return;
    }
    setBusy(true);
    try {
      const recipient = await metadata.lookupUserByEmail(trimmed, session.jwt);
      // Wrap K_album with the recipient's RSA public key. The wrapped key is
      // opaque to the server — only the recipient can unwrap it with their
      // private key.
      const wrappedAlbumKey = await cv.wrapAlbumKeyFor(albumKey, recipient.rsaPubB64);
      await metadata.createShare(
        {
          rootAlbumId,
          recipientUserId: recipient.userId,
          wrappedAlbumKey,
        },
        session.jwt,
      );
      setEmail('');
      await reload();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'errore';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  async function handleRevoke(recipientUserId: string, recipientEmail: string) {
    if (!window.confirm(`Revocare l'accesso a ${recipientEmail}?`)) return;
    try {
      await metadata.deleteShare(rootAlbumId, recipientUserId, session.jwt);
      await reload();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'errore');
    }
  }

  const list = Array.isArray(shares) ? shares : [];

  return (
    <Card className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <Icon name="share" className="text-2xl text-cyan" />
        <h3 className="font-display text-lg font-semibold">Condividi questo album</h3>
      </div>
      <p className="text-xs text-on-surface-variant">
        Condividi in lettura tutto il sotto-albero della radice. La chiave dell'album viene cifrata
        sul tuo device per la chiave pubblica del destinatario — il server non vede mai la chiave in
        chiaro.
      </p>
      <form onSubmit={handleShare} className="flex flex-col gap-3 md:flex-row md:items-end">
        <div className="flex-1">
          <Input
            label="email destinatario"
            type="email"
            placeholder="amico@esempio.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            {...(error ? { error } : {})}
            disabled={busy}
          />
        </div>
        <Button type="submit" loading={busy} leftIcon={<Icon name="person_add" />}>
          Condividi
        </Button>
      </form>

      <div className="flex flex-col gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-[0.08em] text-on-surface-variant">
          Già condiviso con
        </h4>
        {shares === 'loading' ? (
          <p className="text-xs text-on-surface-variant">Carico…</p>
        ) : 'error' in (shares as object) && !Array.isArray(shares) ? (
          <p className="text-xs text-error">Errore: {(shares as { error: string }).error}</p>
        ) : list.length === 0 ? (
          <p className="text-xs text-on-surface-variant">Nessuna condivisione attiva.</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {list.map((s) => (
              <li
                key={s.recipientUserId}
                className="flex items-center justify-between rounded border border-outline px-3 py-2 text-sm"
              >
                <span>{s.recipientEmail}</span>
                <button
                  type="button"
                  onClick={() => void handleRevoke(s.recipientUserId, s.recipientEmail)}
                  className="text-xs font-semibold uppercase tracking-[0.08em] text-error hover:underline"
                >
                  Revoca
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
